import { VoxelAsset, VoxelData } from '../voxelTypes';
import { createNoiseGenerator } from '../noise';
import { PALETTE, getRandomizedColor } from '../voxelColor';
import { generateVoxelCottage } from './VoxelCottage';
import { generateVoxelTree } from './VoxelTree';
import { generateVoxelCropField } from './VoxelCropField';
import { generateVoxelBridge, generateVoxelFenceSegment, generateVoxelSheepPen } from './VoxelFenceAndBridge';
import { generateVoxelWell, generateVoxelWindmill, generateVoxelCart, generateVoxelLanternPole } from './VoxelProps';
import { SheepStyle } from './catalog/PetCatalog';

export interface WorldTerrainData {
  terrainVoxels: VoxelData[];
  cottageAssets: { asset: VoxelAsset; x: number; y: number; z: number; rotation: number }[];
  treeAssets: { asset: VoxelAsset; x: number; y: number; z: number }[];
  cropAssets: { asset: VoxelAsset; x: number; y: number; z: number }[];
  propAssets: { asset: VoxelAsset; x: number; y: number; z: number; rotation?: number }[];
  chimneyPositions: { x: number; y: number; z: number }[];
  homeLightPositions: { x: number; y: number; z: number }[];
  characterPaths: { type: 'farmer' | 'miller' | 'villager', start: {x: number, y: number, z: number}, end: {x: number, y: number, z: number} }[];
  sheepPaths: { style: SheepStyle, start: {x: number, y: number, z: number}, end: {x: number, y: number, z: number} }[];
  waterVoxels: VoxelData[];
  totalVoxelCount: number;
}

export function generateFullVoxelWorld(seed = 42, mapSize = 110): WorldTerrainData {
  const noiseGen = createNoiseGenerator(seed);
  const terrainVoxels: VoxelData[] = [];
  const waterVoxels: VoxelData[] = [];
  const cottageAssets: { asset: VoxelAsset; x: number; y: number; z: number; rotation: number }[] = [];
  const treeAssets: { asset: VoxelAsset; x: number; y: number; z: number }[] = [];
  const cropAssets: { asset: VoxelAsset; x: number; y: number; z: number }[] = [];
  const propAssets: { asset: VoxelAsset; x: number; y: number; z: number; rotation?: number }[] = [];
  const chimneyPositions: { x: number; y: number; z: number }[] = [];
  const homeLightPositions: { x: number; y: number; z: number }[] = [];

  const halfSize = Math.floor(mapSize / 2);

  const rawHeightMap: { [key: string]: number } = {};
  const heightMap: { [key: string]: number } = {};
  const isPathMap: { [key: string]: boolean } = {};
  const isWaterMap: { [key: string]: boolean } = {};

  // River Curve
  const riverCurve = (z: number) => {
    return Math.sin(z * 0.04) * 14 - 22;
  };

  // 1. First Pass: Raw Heights & River Map
  for (let x = -halfSize; x <= halfSize; x++) {
    for (let z = -halfSize; z <= halfSize; z++) {
      const riverX = riverCurve(z);
      const distToRiver = Math.abs(x - riverX);

      let rawHeight = noiseGen.getOctave(x + 500, z + 500, 3, 0.45) * 12;
      let yHeight = Math.max(1, Math.floor(rawHeight));

      if (distToRiver < 4.0) {
        yHeight = 1;
        isWaterMap[`${x},${z}`] = true;
      } else if (distToRiver >= 4.0 && distToRiver < 6.5) {
        yHeight = 2;
      }

      rawHeightMap[`${x},${z}`] = yHeight;
    }
  }

  // Define Building Locations & Watermill right at river base
  const millZ = -18;
  const riverXAtMill = Math.round(riverCurve(millZ));
  const millX = riverXAtMill - 8; // Backed up onto dry shore/land; only the spinning waterwheel extends over the river water

  const wmX = 35, wmZ = 6;

  // Platform definitions for flattening underneath buildings (radius & flat height target)
  const platforms: { x: number; z: number; radius: number; forceY?: number }[] = [
    { x: 14, z: -24, radius: 7 }, // Hillside Hobbit Hole
    { x: 32, z: -16, radius: 7 }, // Highland Cabin
    { x: millX, z: millZ, radius: 8, forceY: 2 }, // Old Watermill sitting right on river base
    { x: 24, z: -2, radius: 7 },  // Valley Hobbit Hole
    { x: 28, z: 32, radius: 8 },  // East Orchard Cottage
    { x: wmX, z: wmZ, radius: 8, forceY: 7 }, // Windmill Tower
    { x: 9, z: 19, radius: 12, forceY: 3 }, // Golden Wheat Farm (centered on 9,19, flat)
    { x: -39, z: 14, radius: 10, forceY: 3 },// Gated Sheep Pasture Pen across river, moved further inland
    { x: 29, z: -27, radius: 8 }, // Pumpkin Patch (centered on 24,-32 width 10, depth 10)
    { x: 19, z: -1, radius: 8 },  // Flower Garden (centered on 14,-6 width 10, depth 10)
    { x: 2, z: 2, radius: 5 },    // Village Well
    { x: 14, z: 14, radius: 5 },  // Farm Cart
  ];

  // Pre-calculate target flat heights for each platform
  const platformTargets = platforms.map(p => {
    const rawY = rawHeightMap[`${p.x},${p.z}`] || 3;
    const targetY = p.forceY !== undefined ? p.forceY : Math.max(2, rawY);
    return { ...p, targetY };
  });

  // 2. Second Pass: Apply Building Flat Pad Smoothing & Fill Heightmap
  for (let x = -halfSize; x <= halfSize; x++) {
    for (let z = -halfSize; z <= halfSize; z++) {
      let finalY = rawHeightMap[`${x},${z}`]!;
      const isRiverTile = isWaterMap[`${x},${z}`];

      if (!isRiverTile) {
        let minDist = 999;
        let matchedTargetY = finalY;

        platformTargets.forEach(p => {
          const d = Math.hypot(x - p.x, z - p.z);
          if (d < minDist) {
            minDist = d;
            if (d <= p.radius) {
              matchedTargetY = p.targetY;
            } else if (d <= p.radius + 3) {
              const t = (d - p.radius) / 3;
              matchedTargetY = Math.round(p.targetY * (1 - t) + finalY * t);
            }
          }
        });

        finalY = matchedTargetY;
      }

      // Winding Dirt Paths
      const distFromCenter = Math.hypot(x - 2, z - 2);
      const isMainRoad = (distFromCenter >= 14 && distFromCenter <= 17) ||
                         (x > -18 && x < 32 && Math.abs(z - (x * 0.3 - 2)) < 2.0);

      const bridgeXLocal = Math.round(riverCurve(0));
      let isWatermillPath = false;
      if (z <= 2 && z >= -24) {
        // Interpolate between z = 0 (x = bridgeXLocal - 5) and z = -18 (x = millX - 3)
        const t = Math.min(1, Math.max(0, z / -18));
        const pathX = (bridgeXLocal - 5) * (1 - t) + (millX - 3) * t + Math.sin(z * 0.25) * 2.0;
        const riverX = riverCurve(z);

        // Flatten the entire landing between the path and the river bank to height 2
        if (x >= pathX - 2.5 && x <= riverX - 4.0) {
          finalY = 2; // Flat pathway matching the bottom of the bridge
          if (Math.abs(x - pathX) < 2.2) {
            isWatermillPath = true;
          }
        }
      }

      if ((isMainRoad || isWatermillPath) && !isRiverTile) {
        isPathMap[`${x},${z}`] = true;
      }

      heightMap[`${x},${z}`] = finalY;
    }
  }

  // 3. Third Pass: Build Solid Contiguous Voxels (y = 0 up to finalY)
  for (let x = -halfSize; x <= halfSize; x++) {
    for (let z = -halfSize; z <= halfSize; z++) {
      const yHeight = heightMap[`${x},${z}`]!;
      const isRiver = isWaterMap[`${x},${z}`];

      if (isRiver) {
        const riverX = riverCurve(z);
        const distToRiver = Math.abs(x - riverX);

        let waterColor = PALETTE.WATER_CYAN_MID;
        if (distToRiver < 1.5) {
          waterColor = PALETTE.WATER_CYAN_DEEP;
        } else if (distToRiver >= 3.0) {
          waterColor = PALETTE.WATER_CYAN_SHALLOW;
        }

        // Foam splashes around bridge (z approx 0) or watermill wheel (z approx -18, x close to the wheel on the left side of the river)
        const isNearBridgeFoam = Math.abs(z) <= 2;
        const isNearMillWheelFoam = Math.abs(z - millZ) <= 2 && (x >= riverX - 4.5 && x <= riverX - 1.0);

        if ((isNearBridgeFoam || isNearMillWheelFoam) && Math.random() > 0.45) {
          waterColor = PALETTE.WATER_FOAM;
        }

        waterVoxels.push({
          x, y: 1, z,
          color: getRandomizedColor(waterColor, 0.02),
          materialType: 'water'
        });

        // Pebbled golden sandy riverbed under river
        terrainVoxels.push({
          x, y: 0, z,
          color: getRandomizedColor((x + z) % 3 === 0 ? PALETTE.PATH_SAND : PALETTE.STONE_MEDIUM, 0.03),
          materialType: 'opaque'
        });
      } else {
        const isRoad = isPathMap[`${x},${z}`];
        for (let y = 0; y <= yHeight; y++) {
          const isSurface = y === yHeight;

          if (isSurface) {
            if (isRoad) {
              terrainVoxels.push({
                x, y, z,
                color: (x + z) % 4 === 0 ? PALETTE.DIRT_MEDIUM : PALETTE.DIRT_LIGHT,
                materialType: 'opaque'
              });
            } else {
              const grassColor = yHeight > 8 ? PALETTE.GRASS_DRY :
                                 (Math.random() > 0.4 ? getRandomizedColor(PALETTE.GRASS_LUSH, 0.02) : PALETTE.GRASS_BRIGHT);
              terrainVoxels.push({
                x, y, z,
                color: grassColor,
                materialType: 'foliage'
              });

              if (Math.random() < 0.003 && yHeight > 2) {
                const flowers = [PALETTE.FLOWER_RED, PALETTE.FLOWER_YELLOW, PALETTE.FLOWER_PURPLE, PALETTE.FLOWER_WHITE];
                terrainVoxels.push({
                  x, y: y + 1, z,
                  color: flowers[Math.floor(Math.random() * flowers.length)]!,
                  materialType: 'foliage'
                });
              }
            }
          } else if (y >= yHeight - 2) {
            terrainVoxels.push({
              x, y, z,
              color: getRandomizedColor(PALETTE.DIRT_MEDIUM, 0.03),
              materialType: 'opaque'
            });
          } else {
            terrainVoxels.push({
              x, y, z,
              color: getRandomizedColor(PALETTE.STONE_MEDIUM, 0.03),
              materialType: 'opaque'
            });
          }
        }
      }
    }
  }

  const getGroundY = (x: number, z: number) => heightMap[`${Math.round(x)},${Math.round(z)}`] || 2;

  // Place Cozy Homes / Hobbit Holes onto 100% flat platforms
  const cottageLocations = [
    { x: 14, z: -24, type: 'hobbit_hole' as const, name: 'Hillside Hobbit Hole', rot: 0 },
    { x: 32, z: -16, type: 'cozy_cabin' as const, name: 'Highland Cabin', rot: -Math.PI / 4 },
    { x: millX, z: millZ, type: 'stone_mill' as const, name: 'Old Watermill', rot: -Math.PI / 6 },
    { x: 24, z: -2, type: 'hobbit_hole' as const, name: 'Valley Hobbit Hole', rot: Math.PI },
    { x: 28, z: 32, type: 'tudor_cottage' as const, name: 'East Orchard Cottage', rot: (2 * Math.PI) / 3 },
  ];

  cottageLocations.forEach((loc, idx) => {
    const groundY = getGroundY(loc.x, loc.z);
    const cottageAsset = generateVoxelCottage(`cottage_${idx}`, loc.name, { type: loc.type });
    cottageAssets.push({
      asset: cottageAsset,
      x: loc.x,
      y: groundY + 1,
      z: loc.z,
      rotation: loc.rot
    });

    // Chimney top smoke position
    chimneyPositions.push({
      x: loc.x + 2,
      y: groundY + (loc.type === 'hobbit_hole' ? 7 : 9),
      z: loc.z - 1
    });

    // Dedicated home illumination light position
    homeLightPositions.push({
      x: loc.x,
      y: groundY + 3,
      z: loc.z + 2
    });
  });

  // Farm Crop Fields
  const farmLocations = [
    { x: 9, z: 19, type: 'wheat_field' as const, name: 'Golden Wheat Farm' },
    { x: 24, z: -32, type: 'pumpkin_patch' as const, name: 'Pumpkin Patch' },
    { x: 14, z: -6, type: 'flower_garden' as const, name: 'Flower Garden' }
  ];

  farmLocations.forEach((farm, idx) => {
    const gy = getGroundY(farm.x, farm.z);
    const cropAsset = generateVoxelCropField(`crop_${idx}`, farm.name, farm.type);
    cropAssets.push({
      asset: cropAsset,
      x: farm.x,
      y: gy + 1,
      z: farm.z
    });
  });

  // Props
  // Windmill Tower sitting on flattened river/hill platform
  const wmY = getGroundY(wmX, wmZ);
  const windmillAsset = generateVoxelWindmill('windmill_1', 'Highland Windmill Tower');
  propAssets.push({ asset: windmillAsset, x: wmX, y: wmY + 1, z: wmZ });
  homeLightPositions.push({ x: wmX, y: wmY + 8, z: wmZ });

  // Water Well in village square
  const wellY = getGroundY(2, 2);
  const wellAsset = generateVoxelWell('well_1', 'Village Square Well');
  propAssets.push({ asset: wellAsset, x: 2, y: wellY + 1, z: 2 });
  homeLightPositions.push({ x: 2, y: wellY + 4, z: 2 });

  // Cart near golden wheat farm (micro half-size cart sitting flush on top of ground at side of field)
  const cartX = 3;
  const cartZ = 19;
  const cartY = getGroundY(cartX, cartZ);
  const cartAsset = generateVoxelCart('cart_1', 'Farm Cart');
  propAssets.push({ asset: cartAsset, x: cartX, y: cartY + 0.55, z: cartZ, rotation: Math.PI / 2 });

  // Gated Sheep Pasture Pen across the river, moved further inland (x: -39, z: 14)
  const penY = getGroundY(-39, 14);
  const sheepPenAsset = generateVoxelSheepPen('sheep_pen_1', 'Gated Sheep Pasture Pen', 16, 12);
  propAssets.push({ asset: sheepPenAsset, x: -39, y: penY + 1, z: 14 });
  homeLightPositions.push({ x: -39, y: penY + 4, z: 14 });

  // Stone Bridge over river at z = 0
  const bridgeX = Math.round(riverCurve(0));
  const bridgeY = getGroundY(bridgeX, 0);
  const bridgeAsset = generateVoxelBridge('bridge_1', 'River Bridge', 10, 4, 'stone');
  propAssets.push({ asset: bridgeAsset, x: bridgeX, y: bridgeY, z: 0 });

  // Guided Lantern Streetlamps along Village Dirt Pathways
  const lanternPathCoords = [
    { x: 4, z: -2 },              // Village Square / Well
    { x: -2, z: 6 },               // North Village Entrance
    { x: bridgeX - 6, z: 2 },      // West Bridge Approach
    { x: bridgeX + 6, z: -2 },     // East Bridge Landing
    { x: 8, z: -16 },              // Pathway to Hillside Hobbit Hole
    { x: 12, z: -22 },             // Hillside Hobbit Hole Gate
    { x: 14, z: 18 },              // Wheat Farm Path Junction
    { x: millX - 2, z: millZ + 4 },// Watermill Dry Shore Pathway
    { x: 10, z: -4 },              // Flower Garden Walkway
    { x: 20, z: -28 },             // Pumpkin Patch Way
    { x: -31, z: 14 },             // Sheep Pasture Gate Entrance
  ];

  lanternPathCoords.forEach((lp, idx) => {
    let lx = lp.x;
    let lz = lp.z;
    // Guarantee lantern is strictly on dry land and away from riverbank
    while (isWaterMap[`${lx},${lz}`] || Math.abs(lx - riverCurve(lz)) < 5.0) {
      lx += lx < riverCurve(lz) ? -2 : 2;
    }
    const ly = getGroundY(lx, lz);
    const lanternAsset = generateVoxelLanternPole(`lantern_${idx}`, `Path Lantern ${idx + 1}`);
    propAssets.push({ asset: lanternAsset, x: lx, y: ly + 1, z: lz });
    homeLightPositions.push({ x: lx, y: ly + 2.5, z: lz });
  });

  // Scatter Trees cleanly with generous spacing (no clumping, no trees in river or houses)
  const treeTypes: ('oak' | 'cherry_blossom' | 'apple_tree' | 'autumn_birch')[] = ['oak', 'oak', 'oak', 'cherry_blossom', 'apple_tree'];
  let treeCount = 0;

  const placedTreeCoords: { x: number; z: number }[] = [];
  const structCoords = [
    ...cottageLocations,
    ...farmLocations,
    { x: -39, z: 14 }, // Gated Sheep Pen
    { x: wmX, z: wmZ },
    { x: 2, z: 2 },
    { x: bridgeX, z: 0 }
  ];

  const gridStep = 14;
  for (let x = -halfSize + 10; x <= halfSize - 10; x += gridStep) {
    for (let z = -halfSize + 10; z <= halfSize - 10; z += gridStep) {
      // Jitter position
      const tx = Math.round(x + (Math.random() * 6 - 3));
      const tz = Math.round(z + (Math.random() * 6 - 3));

      // 1. River Clearance
      const distToRiver = Math.abs(tx - riverCurve(tz));
      if (distToRiver < 9) continue;

      // 2. Road Clearance
      if (isPathMap[`${tx},${tz}`]) continue;

      // 3. Structure Clearance
      const nearStructure = structCoords.some(s => Math.hypot(tx - s.x, tz - s.z) < 10);
      if (nearStructure) continue;

      // 4. Tree-to-Tree Clearance (minimum 12 voxels apart)
      const nearOtherTree = placedTreeCoords.some(t => Math.hypot(tx - t.x, tz - t.z) < 12);
      if (nearOtherTree) continue;

      const gy = getGroundY(tx, tz);
      if (gy >= 1) {
        const type = treeTypes[Math.floor(Math.random() * treeTypes.length)];
        const treeAsset = generateVoxelTree(`tree_${treeCount}`, `Micro ${type}`, type);
        treeAssets.push({
          asset: treeAsset,
          x: tx,
          y: gy + 1,
          z: tz
        });
        placedTreeCoords.push({ x: tx, z: tz });
        treeCount++;
      }
    }
  }

  // 9. Character Paths
  const characterPaths: { type: 'farmer' | 'miller' | 'villager', start: {x: number, y: number, z: number}, end: {x: number, y: number, z: number} }[] = [
    // Farmer walking alongside the wheat field in the direction the river flows, stopping before reaching the hill
    {
      type: 'farmer',
      start: { x: 6, y: getGroundY(6, 15), z: 15 },
      end: { x: 6, y: getGroundY(6, 22), z: 22 }
    },
    // Villager walking across the bridge
    {
      type: 'villager',
      start: { x: bridgeX - 5, y: getGroundY(bridgeX - 5, 0), z: 0 },
      end: { x: bridgeX + 5, y: getGroundY(bridgeX + 5, 0), z: 0 }
    },
    // Miller walking near the mill on land to the left/front of the building
    {
      type: 'miller',
      start: { x: millX - 10, y: getGroundY(millX - 10, millZ + 4), z: millZ + 4 },
      end: { x: millX - 3, y: getGroundY(millX - 3, millZ + 8), z: millZ + 8 }
    }
  ];

  // 10. Sheep Herd Paths inside the Gated Pasture Pen (pen centered at x: -39, z: 14)
  const sy = getGroundY(-39, 14);
  const sheepPaths: { style: SheepStyle, start: {x: number, y: number, z: number}, end: {x: number, y: number, z: number} }[] = [
    {
      style: 'white',
      start: { x: -44, y: sy, z: 10 },
      end: { x: -34, y: sy, z: 17 }
    },
    {
      style: 'suffolk',
      start: { x: -34, y: sy, z: 11 },
      end: { x: -43, y: sy, z: 18 }
    },
    {
      style: 'caramel',
      start: { x: -43, y: sy, z: 15 },
      end: { x: -35, y: sy, z: 12 }
    },
    // Rare Cotton Candy Pink Sheep!
    {
      style: 'pink',
      start: { x: -37, y: sy, z: 17 },
      end: { x: -41, y: sy, z: 10 }
    },
    {
      style: 'white',
      start: { x: -35, y: sy, z: 13 },
      end: { x: -44, y: sy, z: 16 }
    }
  ];

  let totalVoxels = terrainVoxels.length + waterVoxels.length;
  [...cottageAssets, ...treeAssets, ...cropAssets, ...propAssets].forEach(item => {
    totalVoxels += item.asset.voxels.length;
  });

  return {
    terrainVoxels,
    waterVoxels,
    cottageAssets,
    treeAssets,
    cropAssets,
    propAssets,
    chimneyPositions,
    homeLightPositions,
    characterPaths,
    sheepPaths,
    totalVoxelCount: totalVoxels
  };
}
